import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Send, CheckCircle2, Check } from 'lucide-react';
import { contactInfo } from '../mock';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  'Social Media Management',
  'Videoproduktion',
  'Fotoproduktion',
  'Sonstiges',
];

const Contact = () => {
  const [form, setForm] = useState({
    first: '',
    last: '',
    email: '',
    phone: '',
    services: [],
    other: '',
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const toggleService = (opt) => {
    setForm((prev) => {
      const has = prev.services.includes(opt);
      const services = has
        ? prev.services.filter((s) => s !== opt)
        : [...prev.services, opt];
      // Clear "other" text when "Sonstiges" gets unchecked
      const other = has && opt === 'Sonstiges' ? '' : prev.other;
      return { ...prev, services, other };
    });
  };

  const wantsOther = form.services.includes('Sonstiges');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first || !form.last || !form.email || form.services.length === 0) {
      toast.error('Bitte fülle alle Pflichtfelder aus.');
      return;
    }
    if (wantsOther && !form.other.trim()) {
      toast.error('Bitte erläutere kurz was du unter "Sonstiges" verstehst.');
      return;
    }
    setSending(true);

    // Save to our backend (for admin dashboard) — non-blocking, don't fail the whole flow if this errors
    axios.post(`${API}/contact`, {
      first: form.first,
      last: form.last,
      email: form.email,
      phone: form.phone.trim() || null,
      services: form.services,
      other: wantsOther ? form.other.trim() : null,
      message: form.message.trim() || null,
    }).catch(() => { /* silent */ });

    try {
      const servicesList = form.services.join(', ');
      const payload = new FormData();
      payload.append('Vorname', form.first);
      payload.append('Name', form.last);
      payload.append('Email', form.email);
      if (form.phone.trim()) {
        payload.append('Telefon', form.phone.trim());
      }
      payload.append('Gewuenschte Services', servicesList);
      if (wantsOther && form.other.trim()) {
        payload.append('Sonstiges - Details', form.other.trim());
      }
      if (form.message.trim()) {
        payload.append('Nachricht', form.message.trim());
      }
      payload.append('_subject', `Anfrage: ${servicesList} – ${form.first}${form.last ? ' ' + form.last : ''}`);
      payload.append('_template', 'table');
      payload.append('_captcha', 'false');
      payload.append('_replyto', form.email);

      const response = await fetch(`https://formsubmit.co/ajax/${contactInfo.email}`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: payload,
      });

      const data = await response.json();
      if (data.success === 'true' || data.success === true) {
        setSent(true);
        toast.success('Deine Anfrage wurde erfolgreich verschickt!');
        setForm({ first: '', last: '', email: '', phone: '', services: [], other: '', message: '' });
      } else {
        throw new Error(data.message || 'Unbekannter Fehler');
      }
    } catch (err) {
      toast.error('Etwas ist schiefgelaufen. Bitte schreib uns direkt an ' + contactInfo.email);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-charcoal py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="section-title text-center mb-12">Kostenlos anfragen!</h2>

        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <p className="text-zinc-300 text-lg mb-8 leading-relaxed">
              Du möchtest deine Reichweite auf Social Media nachhaltig steigern? Schreib uns – wir melden uns innerhalb von 24 Stunden bei dir!
            </p>

            <div className="space-y-4 mb-8">
              <a
                href={`mailto:${contactInfo.email}`}
                className="inline-flex items-center gap-4 text-zinc-100 hover:text-yellow transition-colors"
              >
                <span className="w-14 h-14 rounded-full bg-yellow flex items-center justify-center shrink-0">
                  <Mail className="w-7 h-7 text-[#1a1a1a]" />
                </span>
                <span className="font-semibold text-xl md:text-2xl break-all">
                  {contactInfo.email}
                </span>
              </a>
            </div>
          </div>

          {sent ? (
            <div className="bg-[#252525] rounded-lg p-8 md:p-10 border border-zinc-800 flex flex-col items-center justify-center text-center min-h-[380px]">
              <div className="w-16 h-16 rounded-full bg-yellow flex items-center justify-center mb-5">
                <CheckCircle2 className="w-9 h-9 text-[#1a1a1a]" />
              </div>
              <h3 className="font-display text-yellow text-2xl md:text-3xl mb-3">
                Danke für deine Anfrage, wir melden uns zeitnah!
              </h3>
              <p className="text-zinc-300 leading-relaxed max-w-md">
                Wir haben deine Nachricht erhalten und melden uns innerhalb von 24 Stunden bei dir.
              </p>
              <button
                type="button"
                onClick={() => setSent(false)}
                className="btn-dark mt-7"
              >
                Neue Anfrage stellen
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-[#252525] rounded-lg p-6 md:p-8 border border-zinc-800">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-yellow text-sm font-semibold" htmlFor="first">Vorname *</label>
                  <input
                    id="first"
                    name="first"
                    value={form.first}
                    onChange={onChange}
                    className="mt-1 w-full bg-transparent border-b-2 border-zinc-600 focus:border-yellow outline-none py-2 text-white"
                  />
                </div>
                <div>
                  <label className="text-yellow text-sm font-semibold" htmlFor="last">Name *</label>
                  <input
                    id="last"
                    name="last"
                    value={form.last}
                    onChange={onChange}
                    className="mt-1 w-full bg-transparent border-b-2 border-zinc-600 focus:border-yellow outline-none py-2 text-white"
                  />
                </div>
              </div>
              <div className="mt-5">
                <label className="text-yellow text-sm font-semibold" htmlFor="email">Email *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  className="mt-1 w-full bg-transparent border-b-2 border-zinc-600 focus:border-yellow outline-none py-2 text-white"
                />
              </div>
              <div className="mt-5">
                <label className="text-yellow text-sm font-semibold" htmlFor="phone">
                  Telefon <span className="text-zinc-500 font-normal">(optional)</span>
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="z. B. 0160 12345678"
                  className="mt-1 w-full bg-transparent border-b-2 border-zinc-600 focus:border-yellow outline-none py-2 text-white placeholder:text-zinc-500"
                />
              </div>

              <div className="mt-5">
                <p className="text-yellow text-sm font-semibold mb-3">
                  Welchen Service? * <span className="text-zinc-500 font-normal">(Mehrfachauswahl möglich)</span>
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {SERVICE_OPTIONS.map((opt) => {
                    const active = form.services.includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => toggleService(opt)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-md border-2 text-left transition-colors ${
                          active
                            ? 'border-yellow bg-yellow/10 text-white'
                            : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'
                        }`}
                      >
                        <span
                          className={`flex-shrink-0 w-5 h-5 rounded flex items-center justify-center border-2 ${
                            active
                              ? 'bg-yellow border-yellow'
                              : 'border-zinc-500 bg-transparent'
                          }`}
                        >
                          {active && <Check className="w-3.5 h-3.5 text-[#1a1a1a]" strokeWidth={3} />}
                        </span>
                        <span className="text-sm font-medium">{opt}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {wantsOther && (
                <div className="mt-5">
                  <label className="text-yellow text-sm font-semibold" htmlFor="other">
                    Sonstiges – bitte erläutern *
                  </label>
                  <textarea
                    id="other"
                    name="other"
                    rows={3}
                    value={form.other}
                    onChange={onChange}
                    placeholder="Was für einen Service wünschst du dir?"
                    className="mt-1 w-full bg-transparent border-b-2 border-zinc-600 focus:border-yellow outline-none py-2 text-white resize-none placeholder:text-zinc-500"
                  />
                </div>
              )}

              <div className="mt-5">
                <label className="text-yellow text-sm font-semibold" htmlFor="message">
                  Nachricht <span className="text-zinc-500 font-normal">(optional)</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={onChange}
                  placeholder="Erzähl uns kurz mehr über dein Projekt..."
                  className="mt-1 w-full bg-transparent border-b-2 border-zinc-600 focus:border-yellow outline-none py-2 text-white resize-none placeholder:text-zinc-500"
                />
              </div>
              <button type="submit" disabled={sending} className="btn-yellow mt-7 disabled:opacity-70">
                {sending ? 'Wird gesendet...' : 'Send'} <Send className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contact;
